import { useState, useEffect, useCallback } from 'react'
import { clsx } from 'clsx'
import type { SharedGroupMember } from '@/lib/sharedTypes'

interface MentionAutocompleteProps {
  query: string
  members: SharedGroupMember[]
  onSelect: (member: SharedGroupMember) => void
  visible: boolean
}

export function MentionAutocomplete({
  query,
  members,
  onSelect,
  visible,
}: MentionAutocompleteProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const filtered = members.filter((m) =>
    m.displayName.toLowerCase().includes(query.toLowerCase())
  )

  // Reset selection when query or visibility changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [query, visible])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!visible || filtered.length === 0) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % filtered.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev - 1 + filtered.length) % filtered.length)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        onSelect(filtered[selectedIndex])
      } else if (e.key === 'Escape') {
        e.preventDefault()
      }
    },
    [visible, filtered, selectedIndex, onSelect]
  )

  useEffect(() => {
    if (!visible) return
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [visible, handleKeyDown])

  if (!visible || filtered.length === 0) return null

  return (
    <div
      className="absolute bottom-full left-0 mb-1 w-64 max-h-40 overflow-y-auto bg-surface-primary border border-base rounded-lg shadow-lg z-30"
    >
      {filtered.map((member, index) => (
        <button
          key={member.uid}
          onClick={() => onSelect(member)}
          className={clsx(
            'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
            index === selectedIndex
              ? 'bg-primary-50 dark:bg-primary-900/20'
              : 'hover:bg-surface-tertiary'
          )}
        >
          {member.photoURL ? (
            <img
              src={member.photoURL}
              alt={member.displayName}
              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-surface-tertiary flex items-center justify-center flex-shrink-0">
              <span className="text-meta font-medium text-caption">
                {member.displayName.charAt(0)}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-heading truncate">
              {member.displayName}
            </p>
            <p className="text-meta text-caption truncate">
              {member.email}
            </p>
          </div>
        </button>
      ))}
    </div>
  )
}
